import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
  getBoreholes,
  deleteBorehole,
  clearBoreholesError,
  clearBoreholesSuccess,
  getBoreholeDetails,
} from "../../../../redux/Slices/boreholesSlice";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSearch,
  FaFilePdf,
  FaFileExcel,
  FaPrint,
} from "react-icons/fa";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import { useTranslation } from "react-i18next";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import { BASE_URL, getToken } from "../../../../shared/utils.js/utils";
import { getDominantColorFromImage } from "../../../../shared/utils.js/legendColor";
import axios from "axios";

const Boreholes = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    list: boreholes,
    isLoading,
    error,
    success,
    pagination: boreholesPagination,
  } = useSelector(state => state.boreholes);
  const { t, i18n } = useTranslation("global");
  const { setTitle } = useTitle();
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("desc");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [boreholeToDelete, setBoreholeToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [boreholeForPrint, setBoreholeForPrint] = useState(null);
  // كل الجسات اللي تبع نفس مشروع الجسة المطلوب طباعتها، عشان تتحط كلها في نفس ملف الـPDF
  const [groupBoreholesForPrint, setGroupBoreholesForPrint] = useState([]);
  // اللون الحقيقي المستخرج من صورة كل رمز، عشان لون المسطرة في التقرير المطبوع يطابق الرمز بالظبط
  const [legendColors, setLegendColors] = useState({});
  const publicUrl = (
    process.env.PUBLIC_URL ||
    (typeof window !== "undefined" ? window.location.origin : "")
  ).replace(/\/$/, "");
  const headerLogo = `${publicUrl}/ACE.png`;
  const pageSize = 15;
  const lastErrorRef = useRef({ message: null, time: 0 });
  const debouncedSearchTermRef = useRef("");
  const currentPageRef = useRef(1);
  const sortByRef = useRef("");
  const sortOrderRef = useRef("desc");
  const [fullBoreholesMap, setFullBoreholesMap] = useState({});
  const [showQuickCreateModal, setShowQuickCreateModal] = useState(false);
  const [quickCreateMode, setQuickCreateMode] = useState("defaults");
  const [quickCreateForm, setQuickCreateForm] = useState({
    project_id: "",
    borehole_number: "",
    selected_borehole_id: "",
  });
  const [projects, setProjects] = useState([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [quickBoreholeOptions, setQuickBoreholeOptions] = useState([]);
  const [isQuickBoreholesLoading, setIsQuickBoreholesLoading] = useState(false);

  const asText = (value, fallback = "-") => {
    if (value === null || value === undefined || value === "") return fallback;
    if (typeof value === "object") {
      return (
        value.name_en ||
        value.name_ar ||
        value.name ||
        value.title ||
        value.code ||
        value.borehole_number ||
        value.project_name ||
        (value.id !== undefined ? String(value.id) : fallback)
      );
    }
    return String(value);
  };

  const asToastText = (value, fallback) => {
    const text = asText(value, "");
    return text || fallback;
  };

  const fetchFullBorehole = async (id, forceRefresh = false) => {
    if (!forceRefresh && fullBoreholesMap[id]) return fullBoreholesMap[id];
    try {
      const response = await dispatch(getBoreholeDetails(id)).unwrap();
      const details = response?.data || response;
      setFullBoreholesMap(prev => ({ ...prev, [id]: details }));
      return details;
    } catch (err) {
      toast.error(
        t("boreholes.fetchDetailsError", {
          defaultValue: "خطأ في تحميل تفاصيل الجسة",
        }),
      );
      return null;
    }
  };

  // بيرجع كل الجسات اللي تحت نفس المشروع (بما فيها الجسة المطلوبة نفسها)، بتفاصيلها الكاملة
  const fetchBoreholeGroup = async (projectId, primaryDetails) => {
    if (!projectId) return [primaryDetails];
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/boreholes?per_page=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = response?.data?.data || [];
      const siblingIds = (Array.isArray(list) ? list : [])
        .filter(item => String(item?.project_id) === String(projectId))
        .map(item => item.id);
      const idsToFetch = siblingIds.length ? siblingIds : [primaryDetails.id];
      const details = await Promise.all(
        idsToFetch.map(id =>
          String(id) === String(primaryDetails.id)
            ? Promise.resolve(primaryDetails)
            : fetchFullBorehole(id, true),
        ),
      );
      return details
        .filter(Boolean)
        .sort((a, b) =>
          String(a.borehole_number ?? "").localeCompare(
            String(b.borehole_number ?? ""),
            undefined,
            { numeric: true },
          ),
        );
    } catch (err) {
      return [primaryDetails];
    }
  };

  useEffect(() => {
    // We are setting the title to a non-existent translation key, it will just show the key or default if not found
    // If you add this to global.json, it will translate correctly.
    setTitle("sidenav.boreholes", { defaultValue: "تقارير جسات التربة" });
  }, [setTitle]);

  useEffect(() => {
    currentPageRef.current = currentPage;
  }, [currentPage]);

  // بنستخرج اللون الحقيقي لكل رمز مستخدم في جسات التقرير المطلوب طباعته من صورته نفسها
  useEffect(() => {
    let cancelled = false;
    const allLayers = groupBoreholesForPrint.flatMap(bh =>
      Array.isArray(bh?.layers) ? bh.layers : [],
    );
    const uniqueLegends = new Map();
    allLayers.forEach(l => {
      const id = l?.legend?.id ?? l?.legend_id;
      const url = getLegendImageUrl(l?.legend);
      if (id != null && url && !uniqueLegends.has(String(id))) {
        uniqueLegends.set(String(id), url);
      }
    });
    uniqueLegends.forEach((url, id) => {
      if (legendColors[id]) return;
      getDominantColorFromImage(url).then(color => {
        if (cancelled || !color) return;
        setLegendColors(prev => (prev[id] ? prev : { ...prev, [id]: color }));
      });
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBoreholesForPrint]);

  const fetchBoreholes = useCallback(
    (page, search, sort, order) => {
      const resolvedPage = page !== undefined ? page : currentPageRef.current;
      const resolvedSearch =
        search !== undefined ? search : debouncedSearchTermRef.current;
      const resolvedSort = sort !== undefined ? sort : sortByRef.current;
      const resolvedOrder = order !== undefined ? order : sortOrderRef.current;
      const params = {
        page: resolvedPage,
        per_page: pageSize,
      };
      if (resolvedSearch.trim()) {
        params.search = resolvedSearch.trim();
      }
      if (resolvedSort) {
        params.sort_by = resolvedSort;
        params.sort_order = resolvedOrder;
      }
      dispatch(getBoreholes(params));
    },
    [dispatch, pageSize],
  );

  useEffect(() => {
    fetchBoreholes(currentPage, debouncedSearchTermRef.current);
  }, [currentPage, fetchBoreholes]);

  useEffect(() => {
    if (searchTerm === debouncedSearchTermRef.current) return undefined;

    const timer = setTimeout(() => {
      debouncedSearchTermRef.current = searchTerm;
      if (currentPageRef.current !== 1) {
        setCurrentPage(1);
      } else {
        fetchBoreholes(1, searchTerm);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, fetchBoreholes]);

  useEffect(() => {
    if (success) {
      toast.success(
        asToastText(
          success,
          t("common.success", { defaultValue: "تمت العملية بنجاح" }),
        ),
        { rtl: isRTL },
      );
      dispatch(clearBoreholesSuccess());
      fetchBoreholes();
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      const errorText = asToastText(
        error,
        t("common.error", { defaultValue: "حدث خطأ غير متوقع" }),
      );
      if (
        !last.message ||
        last.message !== errorText ||
        now - last.time > 2000
      ) {
        toast.error(errorText, { rtl: isRTL });
        lastErrorRef.current = { message: errorText, time: now };
      }
      setTimeout(() => dispatch(clearBoreholesError()), 3000);
    }
  }, [success, error, dispatch, isRTL, fetchBoreholes, t]);

  const handleAdd = () => {
    navigate("/boreholes/add");
  };

  const handleSort = column => {
    if (sortBy === column) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      sortOrderRef.current = newOrder;
      setCurrentPage(1);
      fetchBoreholes(1, debouncedSearchTermRef.current, column, newOrder);
    } else {
      setSortBy(column);
      setSortOrder("asc");
      sortByRef.current = column;
      sortOrderRef.current = "asc";
      setCurrentPage(1);
      fetchBoreholes(1, debouncedSearchTermRef.current, column, "asc");
    }
  };

  const getSortIcon = column => sortBy !== column ? " ↕" : sortOrder === "asc" ? " ↑" : " ↓";

  const loadProjects = async () => {
    setIsProjectsLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/projects?per_page=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data =
        response?.data?.data?.data ||
        response?.data?.data ||
        response?.data ||
        [];
      setProjects(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(
        t("boreholes.quick.projectsError", {
          defaultValue: "تعذر تحميل المشاريع",
        }),
      );
    } finally {
      setIsProjectsLoading(false);
    }
  };

  const openQuickCreateModal = async mode => {
    setQuickCreateMode(mode);
    setQuickCreateForm({
      project_id: "",
      borehole_number: "",
      selected_borehole_id: "",
    });
    setQuickBoreholeOptions([]);
    setShowQuickCreateModal(true);
    await loadProjects();
  };

  const loadQuickBoreholes = async projectId => {
    if (!projectId) {
      setQuickBoreholeOptions([]);
      return;
    }
    setIsQuickBoreholesLoading(true);
    try {
      const token = getToken();
      const response = await axios.get(`${BASE_URL}/boreholes?per_page=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = response?.data?.data || [];
      const filtered = (Array.isArray(list) ? list : []).filter(
        item => String(item?.project_id) === String(projectId),
      );
      setQuickBoreholeOptions(filtered);
    } catch (err) {
      setQuickBoreholeOptions([]);
      toast.error(
        t("boreholes.quick.boreholesError", {
          defaultValue: "تعذر تحميل أرقام الجسات",
        }),
      );
    } finally {
      setIsQuickBoreholesLoading(false);
    }
  };

  const handleQuickCreate = async () => {
    if (
      !quickCreateForm.project_id ||
      !quickCreateForm.borehole_number.trim()
    ) {
      toast.warn(
        t("boreholes.quick.required", {
          defaultValue: "اختر المشروع وادخل رقم الجسة",
        }),
      );
      return;
    }
    try {
      const token = getToken();
      const endpoint =
        quickCreateMode === "defaults"
          ? "/boreholes/from-defaults"
          : "/boreholes/clone-last";
      const response = await axios.post(
        `${BASE_URL}${endpoint}`,
        {
          project_id: Number(quickCreateForm.project_id),
          borehole_number: quickCreateForm.borehole_number.trim(),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const created =
        response?.data?.borehole ||
        response?.data?.data ||
        response?.data ||
        {};
      toast.success(
        t("boreholes.quick.success", { defaultValue: "تم إنشاء الجسة بنجاح" }),
      );
      setShowQuickCreateModal(false);
      fetchBoreholes();
      if (created?.id) navigate(`/boreholes/edit/${created.id}`);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          t("boreholes.quick.error", {
            defaultValue: "فشل إنشاء الجسة السريعة",
          }),
      );
    }
  };

  const handleEdit = id => {
    navigate(`/boreholes/edit/${id}`);
  };

  const handleView = id => {
    navigate(`/boreholes/view/${id}`);
  };

  const handleDelete = borehole => {
    setBoreholeToDelete(borehole);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (boreholeToDelete) {
      await dispatch(deleteBorehole(boreholeToDelete.id));
      setShowDeleteModal(false);
      setBoreholeToDelete(null);
    }
  };

  const boreholesList = Array.isArray(boreholes)
    ? boreholes
    : Array.isArray(boreholes?.data)
      ? boreholes.data
      : Array.isArray(boreholes?.data?.data)
        ? boreholes.data.data
        : [];

  // لما كذا جسة متتالية تبع نفس المشروع، بندمج خانة اسم المشروع/المالك بتاعتهم بصريًا (rowSpan)
  // بدل ما يبانوا كأنهم مشاريع منفصلة. لو مش متتاليين (اترتبوا بينهم جسة مشروع تاني) بيفضلوا منفصلين.
  const projectRowSpans = boreholesList.map((b, i) => {
    const projectId = b?.project_id;
    if (projectId == null) return 1;
    if (i > 0 && String(boreholesList[i - 1]?.project_id) === String(projectId)) {
      return 0; // صف امتداد، مغطى برقم الصف الأول في المجموعة
    }
    let span = 1;
    for (let j = i + 1; j < boreholesList.length; j++) {
      if (String(boreholesList[j]?.project_id) === String(projectId)) span++;
      else break;
    }
    return span;
  });

  // Get pagination info from API
  const pagination = boreholesPagination || {};
  const totalPages = pagination.last_page || 1;
  const currentPageSafe = pagination.current_page || currentPage || 1;
  const from = pagination.from || 1;

  const shouldShowPagination =
    boreholesList.length > 0 &&
    (totalPages > 0 || boreholesPagination !== null);

  const getBoreholeName = borehole => {
    if (!borehole || typeof borehole !== "object") {
      return t("boreholes.unnamed", { defaultValue: "بإسم غير معروف" });
    }
    return (
      asText(
        borehole.project_name || borehole.project || borehole.project_id,
        "",
      ) ||
      asText(borehole.borehole_number, "") ||
      t("boreholes.unnamed", { defaultValue: "بإسم غير معروف" })
    );
  };

  const handleExportAllToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      boreholesList.map((c, i) => ({
        [isRTL ? "م" : "No."]: from + i,
        [isRTL ? "اسم المشروع" : "Project Name"]: asText(
          c.project_name || c.project || c.project_id,
        ),
        [isRTL ? "المالك" : "Owner"]: asText(c.owner),
        [isRTL ? "المقاول الرئيسي" : "Main Contractor"]: asText(
          c.main_contractor,
        ),
        [isRTL ? "الموقع" : "Location"]: asText(c.location),
        [isRTL ? "الاستشاري" : "Consultant"]: asText(c.consultant),
        [isRTL ? "طريقة الحفر" : "Boring Method"]: asText(c.boring_method),
        [isRTL ? "القطر" : "Diameter"]: asText(c.diameter),
        [isRTL ? "منسوب المياه" : "Groundwater Level"]: asText(
          c.groundwater_level,
        ),
        [isRTL ? "رقم المرجع" : "ACE Ref No"]: asText(c.ace_ref_no),
        [isRTL ? "رقم الجسة" : "BH No"]: asText(c.borehole_number),
        [isRTL ? "العمق (م)" : "Total Depth (m)"]: asText(c.total_depth),
        [isRTL ? "تاريخ التقرير" : "Report Date"]: asText(c.report_date),
        [isRTL ? "تاريخ الإنشاء" : "Created At"]: c.created_at
          ? new Date(c.created_at).toLocaleDateString()
          : "-",
        [isRTL ? "ملاحظات" : "Notes"]: asText(c.notes),
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Boreholes List");
    XLSX.writeFile(
      wb,
      `Boreholes_All_Data_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const handleExportBoreholeToExcel = async boreholeId => {
    setIsGeneratingPdf(true); // Reuse loading state
    try {
      const details = await fetchFullBorehole(boreholeId);
      if (!details) return;

      const wb = XLSX.utils.book_new();

      // Sheet 1: Main Info
      const mainData = [
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "القيمة" : "Value",
          "": "",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "اسم المشروع" : "Project Name",
          [isRTL ? "القيمة" : "Value"]: asText(
            details.project_name || details.project || details.project_id,
          ),
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "المالك" : "Owner",
          [isRTL ? "القيمة" : "Value"]: details.owner || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL
            ? "المقاول الرئيسي"
            : "Main Contractor",
          [isRTL ? "القيمة" : "Value"]: details.main_contractor || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "الموقع" : "Location",
          [isRTL ? "القيمة" : "Value"]: details.location || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "الاستشاري" : "Consultant",
          [isRTL ? "القيمة" : "Value"]: details.consultant || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "طريقة الحفر" : "Boring Method",
          [isRTL ? "القيمة" : "Value"]: details.boring_method || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "القطر" : "Diameter",
          [isRTL ? "القيمة" : "Value"]: details.diameter || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL
            ? "منسوب المياه"
            : "Groundwater Level",
          [isRTL ? "القيمة" : "Value"]: details.groundwater_level || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "رقم المرجع" : "ACE Ref No",
          [isRTL ? "القيمة" : "Value"]: details.ace_ref_no || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "رقم الجسة" : "Borehole Number",
          [isRTL ? "القيمة" : "Value"]: details.borehole_number || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL
            ? "العمق الإجمالي (م)"
            : "Total Depth (m)",
          [isRTL ? "القيمة" : "Value"]: details.total_depth || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "تاريخ التقرير" : "Report Date",
          [isRTL ? "القيمة" : "Value"]: details.report_date || "-",
        },
        {
          [isRTL ? "المجال" : "Field"]: isRTL ? "ملاحظات" : "Notes",
          [isRTL ? "القيمة" : "Value"]: details.notes || "-",
        },
      ];

      const wsMain = XLSX.utils.json_to_sheet(mainData, { skipHeader: true });
      XLSX.utils.book_append_sheet(
        wb,
        wsMain,
        isRTL ? "معلومات الجسة" : "Borehole Info",
      );

      // Sheet 2: Layers Info
      if (details.layers && details.layers.length > 0) {
        const layersData = details.layers.map((l, i) => ({
          [isRTL ? "م" : "No."]: i + 1,
          [isRTL ? "من عمق (م)" : "Start Depth (m)"]: l.start_depth || 0,
          [isRTL ? "إلى عمق (م)" : "End Depth (m)"]: l.end_depth || 0,
          [isRTL ? "السماكة (م)" : "Thickness (m)"]: l.thickness || 0,
          [isRTL ? "وصف الطبقة / الأسطورة" : "Layer Description / Legend"]:
            getLegendLabel(l, isRTL) || l.layer_description || "-",
          "SPT N": l.spt_n || "-",
          [isRTL ? "وصف تفصيلي" : "Detailed Description"]:
            l.layer_description || "-",
        }));
        const wsLayers = XLSX.utils.json_to_sheet(layersData);
        XLSX.utils.book_append_sheet(
          wb,
          wsLayers,
          isRTL ? "بيانات الطبقات" : "Layers Data",
        );
      }

      XLSX.writeFile(
        wb,
        `Borehole_Full_Data_${details.borehole_number || details.id}.xlsx`,
      );
      toast.success(
        t("common.exportSuccess", { defaultValue: "تم تصدير البيانات بنجاح" }),
      );
    } catch (err) {
      console.error(err);
      toast.error(
        t("common.exportError", {
          defaultValue: "حدث خطأ أثناء تصدير البيانات",
        }),
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const saveCanvasAsMultiPagePdf = (canvas, fileName) => {
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgData = canvas.toDataURL("image/jpeg", 0.95);
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
    heightLeft -= pdfHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
    }

    pdf.save(fileName);
  };

  const handlePrintAll = async () => {
    setIsGeneratingPdf(true);
    try {
      await new Promise(r => setTimeout(r, 500)); // Wait for render
      const element = document.getElementById("all-boreholes-pdf");
      if (!element) throw new Error("PDF template not found");

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(
        canvas.toDataURL("image/jpeg", 0.95),
        "JPEG",
        0,
        0,
        pdfWidth,
        Math.min(pdfHeight, pdf.internal.pageSize.getHeight()),
      );
      pdf.save(`Boreholes_List_${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success(
        t("common.downloadSuccess", { defaultValue: "تم تحميل الملف بنجاح" }),
      );
    } catch (err) {
      console.error(err);
      toast.error(
        t("common.printError", { defaultValue: "حدث خطأ أثناء الطباعة" }),
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePrintBorehole = async boreholeId => {
    setIsGeneratingPdf(true);
    try {
      const details =
        fullBoreholesMap[boreholeId] || (await fetchFullBorehole(boreholeId, true));
      if (!details) return;
      setBoreholeForPrint(details);

      let group = groupBoreholesForPrint;
      if (!group.length || !group.some(b => String(b.id) === String(boreholeId))) {
        group = await fetchBoreholeGroup(details.project_id, details);
        setGroupBoreholesForPrint(group);
      }

      await new Promise(r => setTimeout(r, 800)); // Wait for template to populate
      const element = document.getElementById("borehole-group-pdf");
      if (!element) throw new Error("PDF template not found");

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const fileLabel =
        group.length > 1
          ? getDisplayValue(details.project_name || details.project)
          : details.borehole_number || details.id;

      saveCanvasAsMultiPagePdf(canvas, `Borehole_${fileLabel}.pdf`);
      toast.success(
        t("common.downloadSuccess", { defaultValue: "تم تحميل الملف بنجاح" }),
      );
    } catch (err) {
      console.error(err);
      toast.error(
        t("common.printError", { defaultValue: "حدث خطأ أثناء الطباعة" }),
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handlePreviewSingle = async borehole => {
    setPdfType("single");
    setIsGeneratingPdf(true);
    try {
      const details = await fetchFullBorehole(borehole.id, true);
      if (!details) return;
      setBoreholeForPrint(details);
      const group = await fetchBoreholeGroup(
        details.project_id ?? borehole.project_id,
        details,
      );
      setGroupBoreholesForPrint(group);
      setIsPreviewOpen(true);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Removed full-page isLoading return to prevent losing focus on search

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            marginBottom: "20px",
            color: "white",
            textAlign,
          }}
        >
          {t("boreholes.title", { defaultValue: "تقارير جسات التربة" })}
        </h1>

        <div
          style={{
            display: "flex",
            gap: "20px",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ position: "relative", flex: "1", minWidth: "300px" }}>
            <FaSearch
              style={{
                position: "absolute",
                [isRTL ? "left" : "right"]: "15px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#666",
                fontSize: "16px",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              placeholder={t("searchPlaceholder", { defaultValue: "ابحث..." })}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: "100%",
                padding: isRTL ? "12px 45px 12px 15px" : "12px 15px 12px 45px",
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
                boxSizing: "border-box",
              }}
            />
          </div>

          <button
            onClick={() => {
              setPdfType("all");
              setBoreholeForPrint(null);
              setIsPreviewOpen(true);
            }}
            style={{
              backgroundColor: "#444",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaEye />
            {t("boreholes.preview.all", { defaultValue: "عرض PDF للكل" })}
          </button>

          <button
            onClick={handlePrintAll}
            disabled={isGeneratingPdf}
            style={{
              backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              cursor: isGeneratingPdf ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              opacity: isGeneratingPdf ? 0.6 : 1,
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaPrint />
            {isGeneratingPdf
              ? t("boreholes.print.generating", {
                  defaultValue: "جاري الطباعة...",
                })
              : t("boreholes.print.all", { defaultValue: "طباعة الكل" })}
          </button>

          <button
            onClick={handleExportAllToExcel}
            style={{
              backgroundColor: "#28a745",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaFileExcel />
            {t("boreholes.export.all", { defaultValue: "تصدير Excel الكل" })}
          </button>

          <button
            onClick={handleAdd}
            style={{
              backgroundColor: "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexDirection: isRTL ? "row" : "row-reverse",
            }}
          >
            <FaPlus />
            {t("boreholes.add", { defaultValue: "إضافة تقرير جسة" })}
          </button>

          <button
            onClick={() => openQuickCreateModal("defaults")}
            style={{
              backgroundColor: "#0f766e",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {t("boreholes.quick.addFromDefaults", {
              defaultValue: "Add from Defaults",
            })}
          </button>

          <button
            onClick={() => openQuickCreateModal("clone")}
            style={{
              backgroundColor: "#7c3aed",
              color: "white",
              border: "none",
              padding: "12px 20px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {t("boreholes.quick.cloneLast", { defaultValue: "Clone Last" })}
          </button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid #333",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "1000px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#AC2000" }}>
                <th style={{ ...thStyle, cursor: "pointer", width: 60 }} onClick={() => handleSort("id")}>
                  {t("table.number", { defaultValue: "م" })}{getSortIcon("id")}
                </th>
                {[
                  { key: null, label: t("boreholes.project_name", { defaultValue: "اسم المشروع" }) },
                  { key: "owner", label: t("boreholes.owner", { defaultValue: "المالك" }) },
                  { key: "location", label: t("boreholes.location", { defaultValue: "الموقع" }) },
                  { key: "consultant", label: t("boreholes.consultant", { defaultValue: "الاستشاري" }) },
                  { key: "boring_method", label: t("boreholes.boring_method", { defaultValue: "طريقة الحفر" }) },
                  { key: "diameter", label: t("boreholes.diameter", { defaultValue: "القطر" }) },
                  { key: "groundwater_level", label: t("boreholes.groundwater_level", { defaultValue: "منسوب المياه" }) },
                  { key: "ace_ref_no", label: t("boreholes.ace_ref_no", { defaultValue: "رقم المرجع" }) },
                  { key: "borehole_number", label: t("boreholes.borehole_number", { defaultValue: "رقم الجسة" }) },
                  { key: "total_depth", label: t("boreholes.total_depth", { defaultValue: "العمق (م)" }) },
                  { key: "report_date", label: t("boreholes.report_date", { defaultValue: "تاريخ التقرير" }) },
                  { key: null, label: t("table.actions", { defaultValue: "الإجراءات" }) },
                ].map(col => (
                  <th
                    key={col.label}
                    style={{ ...thStyle, cursor: col.key ? "pointer" : "default" }}
                    onClick={col.key ? () => handleSort(col.key) : undefined}
                  >
                    {col.label}{col.key ? getSortIcon(col.key) : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {boreholesList.length > 0 ? (
                boreholesList.map((borehole, index) => (
                  <tr
                    key={borehole.id}
                    style={{ borderBottom: "1px solid #333" }}
                  >
                    <td style={tdStyle}>{from + index}</td>
                    {projectRowSpans[index] > 0 && (
                      <td style={tdStyle} rowSpan={projectRowSpans[index]}>
                        {asText(
                          borehole.project_name ||
                            borehole.project ||
                            borehole.project_id,
                        )}
                      </td>
                    )}
                    {projectRowSpans[index] > 0 && (
                      <td style={tdStyle} rowSpan={projectRowSpans[index]}>
                        {asText(borehole.owner)}
                      </td>
                    )}
                    <td style={tdStyle}>{asText(borehole.location)}</td>
                    <td style={tdStyle}>{asText(borehole.consultant)}</td>
                    <td style={tdStyle}>{asText(borehole.boring_method)}</td>
                    <td style={tdStyle}>{asText(borehole.diameter)}</td>
                    <td style={tdStyle}>
                      {asText(borehole.groundwater_level)}
                    </td>
                    <td style={tdStyle}>{asText(borehole.ace_ref_no)}</td>
                    <td style={tdStyle}>{asText(borehole.borehole_number)}</td>
                    <td style={tdStyle}>{asText(borehole.total_depth)}</td>
                    <td style={tdStyle}>{asText(borehole.report_date)}</td>
                    <td
                      style={{
                        padding: "15px",
                        textAlign: "center",
                        minWidth: "200px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          gap: "8px",
                          flexWrap: "nowrap",
                        }}
                      >
                        <ActionButton
                          title={t("btns.view", { defaultValue: "عرض الرسم" })}
                          onClick={() => handleView(borehole.id)}
                        >
                          <FaEye />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.edit", { defaultValue: "تعديل" })}
                          onClick={() => handleEdit(borehole.id)}
                        >
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title={t("boreholes.print.single", {
                            defaultValue: "طباعة",
                          })}
                          onClick={() => handlePreviewSingle(borehole)}
                          disabled={isGeneratingPdf}
                          bg={isGeneratingPdf ? "#666" : "#17a2b8"}
                        >
                          <FaPrint />
                        </ActionButton>
                        <ActionButton
                          title={t("boreholes.export.single", {
                            defaultValue: "تصدير Excel",
                          })}
                          onClick={() =>
                            handleExportBoreholeToExcel(borehole.id)
                          }
                          disabled={isGeneratingPdf}
                          bg="#28a745"
                        >
                          <FaFileExcel />
                        </ActionButton>
                        <ActionButton
                          title={t("btns.delete", { defaultValue: "حذف" })}
                          bg="#c0392b"
                          onClick={() => handleDelete(borehole)}
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              ) : isLoading && boreholesList.length === 0 ? (
                <tr>
                  <td
                    colSpan="13"
                    style={{
                      textAlign: "center",
                      color: "#bbb",
                      padding: "20px",
                    }}
                  >
                    <div
                      className="spinner-border text-primary"
                      role="status"
                      style={{ width: "1.5rem", height: "1.5rem" }}
                    >
                      <span className="visually-hidden">
                        {t("loading", { defaultValue: "جاري التحميل..." })}
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr>
                  <td
                    colSpan="13"
                    style={{
                      textAlign: "center",
                      color: "white",
                      padding: "20px",
                    }}
                  >
                    {t("noData", { defaultValue: "لا توجد بيانات" })}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {shouldShowPagination &&
          (() => {
            const pages = [];
            for (let i = 1; i <= totalPages; i++) {
              if (
                i === 1 ||
                i === totalPages ||
                (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
              ) {
                pages.push(i);
              } else if (
                i === currentPageSafe - 3 ||
                i === currentPageSafe + 3
              ) {
                pages.push("...");
              }
            }
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "20px",
                  paddingBottom: "20px",
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() =>
                    setCurrentPage(Math.max(1, currentPageSafe - 1))
                  }
                  disabled={currentPageSafe === 1}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                    opacity: currentPageSafe === 1 ? 0.5 : 1,
                  }}
                >
                  {t("pagination.previous", { defaultValue: "السابق" })}
                </button>
                {pages.map((p, idx) =>
                  p === "..." ? (
                    <span
                      key={`ellipsis-${idx}`}
                      style={{ padding: "8px", color: "white" }}
                    >
                      ...
                    </span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      style={{
                        padding: "8px 12px",
                        backgroundColor:
                          p === currentPageSafe ? "#AC2000" : "#1a1f2e",
                        color: "white",
                        border: "1px solid #333",
                        borderRadius: "4px",
                        cursor: "pointer",
                      }}
                    >
                      {p}
                    </button>
                  ),
                )}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                  }
                  disabled={currentPageSafe === totalPages}
                  style={{
                    padding: "8px 12px",
                    backgroundColor:
                      currentPageSafe === totalPages ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      currentPageSafe === totalPages
                        ? "not-allowed"
                        : "pointer",
                    opacity: currentPageSafe === totalPages ? 0.5 : 1,
                  }}
                >
                  {t("pagination.next", { defaultValue: "التالي" })}
                </button>
              </div>
            );
          })()}
      </div>

      {showDeleteModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: "#1b3350",
                border: "1px solid #333",
                borderRadius: "12px",
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              }}
            >
              <div
                className="modal-header"
                style={{ border: "none", borderBottom: "1px solid #333" }}
              >
                <h5 className="modal-title text-white" style={{ textAlign }}>
                  {t("delete.title", { defaultValue: "حذف" })}
                </h5>
              </div>
              <div className="modal-body text-white" style={{ textAlign }}>
                <p>
                  {t("delete.message", { defaultValue: "هل أنت متأكد من حذف" })}{" "}
                  "{getBoreholeName(boreholeToDelete)}"؟
                </p>
                <p style={{ color: "#dc3545" }}>
                  {t("delete.irreversible", {
                    defaultValue: "هذا الإجراء لا يمكن التراجع عنه.",
                  })}
                </p>
              </div>
              <div className="modal-footer" style={{ border: "none" }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteModal(false)}
                  style={{
                    backgroundColor: "#6c757d",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("delete.cancel", { defaultValue: "إلغاء" })}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: "#dc3545",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 20px",
                  }}
                >
                  {t("delete.confirm", { defaultValue: "تأكيد الحذف" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQuickCreateModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: "#1b3350",
                color: "white",
                border: "1px solid #333",
              }}
            >
              <div
                className="modal-header"
                style={{ borderBottom: "1px solid #333" }}
              >
                <h5 className="modal-title">
                  {quickCreateMode === "defaults"
                    ? t("boreholes.quick.addFromDefaults", {
                        defaultValue: "Add from Defaults",
                      })
                    : t("boreholes.quick.cloneLast", {
                        defaultValue: "Clone Last",
                      })}
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowQuickCreateModal(false)}
                />
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: 12 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#cfd6e4",
                      fontSize: 13,
                    }}
                  >
                    {t("boreholes.project_name", {
                      defaultValue: "اسم المشروع",
                    })}
                  </label>
                  <select
                    value={quickCreateForm.project_id}
                    onChange={async e => {
                      const selectedProjectId = e.target.value;
                      setQuickCreateForm(prev => ({
                        ...prev,
                        project_id: selectedProjectId,
                        borehole_number: "",
                        selected_borehole_id: "",
                      }));
                      await loadQuickBoreholes(selectedProjectId);
                    }}
                    style={quickInputStyle}
                    disabled={isProjectsLoading}
                  >
                    <option value="">
                      {isProjectsLoading
                        ? t("loading", { defaultValue: "جاري التحميل..." })
                        : t("boreholes.quick.selectProject", {
                            defaultValue: "اختر المشروع",
                          })}
                    </option>
                    {projects.map(project => (
                      <option key={project.id} value={project.id}>
                        {project.name_ar ||
                          project.name_en ||
                          project.name ||
                          `#${project.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 6,
                      color: "#cfd6e4",
                      fontSize: 13,
                    }}
                  >
                    {t("boreholes.borehole_number", {
                      defaultValue: "رقم الجسة",
                    })}
                  </label>
                  <select
                    value={quickCreateForm.selected_borehole_id}
                    onChange={e => {
                      const selectedId = e.target.value;
                      const selected = quickBoreholeOptions.find(
                        item => String(item.id) === String(selectedId),
                      );
                      setQuickCreateForm(prev => ({
                        ...prev,
                        selected_borehole_id: selectedId,
                        borehole_number: selected?.borehole_number || "",
                      }));
                    }}
                    style={quickInputStyle}
                    disabled={
                      !quickCreateForm.project_id || isQuickBoreholesLoading
                    }
                  >
                    <option value="">
                      {isQuickBoreholesLoading
                        ? t("loading", { defaultValue: "جاري التحميل..." })
                        : t("boreholes.quick.selectBorehole", {
                            defaultValue: "اختر رقم الجسة من الجدول",
                          })}
                    </option>
                    {quickBoreholeOptions.map(item => (
                      <option key={item.id} value={item.id}>
                        {`${item.borehole_number || `#${item.id}`} (${t("table.number", { defaultValue: "م" })}: ${item.id})`}
                      </option>
                    ))}
                  </select>
                  <input
                    value={quickCreateForm.borehole_number}
                    onChange={e =>
                      setQuickCreateForm(prev => ({
                        ...prev,
                        borehole_number: e.target.value,
                      }))
                    }
                    style={quickInputStyle}
                    placeholder={t("boreholes.quick.numberPrompt", {
                      defaultValue: "ادخل رقم الجسة:",
                    })}
                    disabled={!quickCreateForm.project_id}
                  />
                </div>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "1px solid #333" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowQuickCreateModal(false)}
                >
                  {t("delete.cancel", { defaultValue: "إلغاء" })}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ backgroundColor: "#AC2000", border: "none" }}
                  onClick={handleQuickCreate}
                >
                  {t("common.create", { defaultValue: "إنشاء" })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern PDF Templates for Capture (Hidden) */}
      <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
        {/* Template for All Boreholes List */}
        <AllBoreholesPrintTemplate
          boreholes={boreholesList}
          isRTL={isRTL}
          t={t}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          from={from}
          searchTerm={searchTerm}
        />

        {/* Hidden template(s) for the selected borehole + sibling boreholes from the same project, combined in one PDF */}
        {groupBoreholesForPrint.length > 0 && (
          <div id="borehole-group-pdf" style={{ width: "794px" }}>
            {groupBoreholesForPrint.map(bh => (
              <SingleBoreholePrintTemplate
                key={bh.id}
                borehole={bh}
                isRTL={isRTL}
                t={t}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                legendColors={legendColors}
                groupMode
              />
            ))}
          </div>
        )}
      </div>

      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          (pdfType === "all"
            ? boreholesList.length > 0
            : !!boreholeForPrint?.id)
        }
        title={
          pdfType === "all"
            ? t("boreholes.print.all", { defaultValue: "طباعة الكل" })
            : t("boreholes.preview.single", { defaultValue: "معاينة الجسة" })
        }
        onClose={() => {
          setIsPreviewOpen(false);
          if (pdfType === "single") {
            setBoreholeForPrint(null);
            setGroupBoreholesForPrint([]);
          }
        }}
        onPrint={
          pdfType === "all"
            ? handlePrintAll
            : () => handlePrintBorehole(boreholeForPrint?.id)
        }
        isGeneratingPdf={isGeneratingPdf}
      >
        {pdfType === "all" ? (
          <AllBoreholesPrintTemplate
            boreholes={boreholesList}
            isRTL={isRTL}
            t={t}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            from={from}
            searchTerm={searchTerm}
            isPreview={true}
          />
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {groupBoreholesForPrint.map(bh => (
              <SingleBoreholePrintTemplate
                key={bh.id}
                borehole={bh}
                isRTL={isRTL}
                t={t}
                publicUrl={publicUrl}
                headerLogo={headerLogo}
                legendColors={legendColors}
                isPreview={true}
                groupMode
              />
            ))}
          </div>
        )}
      </PdfPreviewModal>
    </div>
  );
};

// --- Helper: Get Layer Color ---
// ملحوظة: l.legend بقى object (العلاقة legend()) مش نص، فلازم نستخرج النص منه الأول
const getLayerColor = legend => {
  const lg = typeof legend === "string" ? legend.toLowerCase() : "";
  if (lg.includes("sand") || lg.includes("رمل")) return "#c2b280";
  if (lg.includes("clay") || lg.includes("طين")) return "#6e5a4e";
  if (lg.includes("rock") || lg.includes("صخر")) return "#888c8d";
  if (lg.includes("silt") || lg.includes("طمي")) return "#a59371";
  if (lg.includes("gravel") || lg.includes("زلط") || lg.includes("حصى"))
    return "#9aa0a6";
  return "#8a7d65"; // default soil
};

// --- Helper: Get the borehole legend's text label (handles the eager-loaded legend relation object) ---
const getLegendLabel = (layer, isRTL) => {
  const legend = layer?.legend;
  if (legend && typeof legend === "object") {
    return (isRTL ? legend.label_ar || legend.label_en : legend.label_en || legend.label_ar) || "";
  }
  return typeof legend === "string" ? legend : "";
};

// --- Helper: Resolve a legend's uploaded pattern image to a full URL ---
const getLegendImageUrl = legend => {
  if (!legend || typeof legend !== "object" || !legend.id) return null;
  // مسار التخزين المباشر (public/storage) مش متظبط على السيرفر، فبنستخدم الراوت المخصص لعرض صورة الرمز
  if (legend.image_path && /^https?:\/\//i.test(legend.image_path)) return legend.image_path;
  return `${BASE_URL}/legends/${legend.id}/image`;
};

const getDisplayValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (typeof value === "object") {
    return (
      value.name_en ||
      value.name_ar ||
      value.name ||
      value.title ||
      value.code ||
      value.borehole_number ||
      value.project_name ||
      (value.id !== undefined ? String(value.id) : fallback)
    );
  }
  return String(value);
};

// بعض الحقول ممكن تحتوي أكتر من قراءة لنفس الطبقة (مثلاً أكتر من قيمة SPT-N)، متخزنة كنص مفصول بفاصلة
const renderMultiValues = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const parts = String(value)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length === 0) return "-";
  if (parts.length === 1) return parts[0];
  return parts.map((p, i) => <div key={i}>{p}</div>);
};

// --- COMPONENT: Single Borehole Report Template ---
const SingleBoreholePrintTemplate = ({
  borehole,
  isRTL,
  t,
  publicUrl,
  headerLogo,
  isPreview = false,
  // groupMode: بيتقال لما الجسة دي بتترسم جوه حاوية مشتركة مع جسات تانية من نفس المشروع
  // (بدل ما كل جسة تاخد position مطلق بمفردها وتتراكب فوق بعض)
  groupMode = false,
  // اللون الحقيقي المستخرج من صورة كل رمز (legend_id -> hex)، عشان لون المسطرة يطابق الرمز بالظبط
  legendColors = {},
}) => {
  if (!borehole) return null;
  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";
  const layers = Array.isArray(borehole.layers) ? borehole.layers : [];
  const boreholeNo = getDisplayValue(borehole.borehole_number || borehole.id);
  const layersPerPage = 3;
  const rulerHeight = 520;
  const boundaryLineWidth = 2;
  // Keep ruler boundary lines aligned with table row separators without hardcoded pixels.
  const boundaryAlignmentOffset = Math.max(1, Math.round(boundaryLineWidth));

  const formatDate = value => {
    if (!value) return "-";
    const d = new Date(value);
    return Number.isNaN(d.getTime())
      ? getDisplayValue(value)
      : d.toLocaleDateString("en-US");
  };

  const toNumber = value => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  const getTickStep = depth => {
    if (depth <= 20) return 1;
    if (depth <= 50) return 5;
    if (depth <= 100) return 10;
    if (depth <= 200) return 20;
    return 50;
  };

  const sortedLayers = [...layers].sort((a, b) => {
    const aEnd = toNumber(a?.end_depth) ?? 0;
    const bEnd = toNumber(b?.end_depth) ?? 0;
    return aEnd - bEnd;
  });

  const chunkedLayers = [];
  for (let i = 0; i < sortedLayers.length; i += layersPerPage) {
    chunkedLayers.push(sortedLayers.slice(i, i + layersPerPage));
  }

  const pages = (chunkedLayers.length ? chunkedLayers : [[]]).map(
    (chunk, pageIndex) => {
      let prevEnd = toNumber(chunk[0]?.start_depth) ?? 0;
      const normalized = chunk.map(layer => {
        const startRaw = toNumber(layer?.start_depth);
        const endRaw = toNumber(layer?.end_depth);
        const start = startRaw !== null ? startRaw : prevEnd;
        const end = endRaw !== null ? Math.max(endRaw, start) : start;
        prevEnd = end;
        return { layer, start, end };
      });

      const pageStart = normalized.length > 0 ? normalized[0].start : 0;
      const pageEnd =
        normalized.length > 0 ? normalized[normalized.length - 1].end : 1;
      const pageRange = Math.max(1, pageEnd - pageStart);
      const tickStep = getTickStep(pageRange);
      const tickValues = [];
      for (let d = pageStart; d <= pageEnd; d += tickStep) {
        tickValues.push(d);
      }
      if (tickValues[tickValues.length - 1] !== pageEnd) {
        tickValues.push(pageEnd);
      }

      const prepared = normalized.map(item => {
        const depthSpan = Math.max(0, item.end - item.start);
        const renderHeight = Math.max(1, (depthSpan / pageRange) * rulerHeight);
        return { ...item, renderHeight };
      });
      let cumulativeHeight = 0;
      const boundaryOffsets = prepared.map(p => {
        cumulativeHeight += p.renderHeight;
        return { top: cumulativeHeight, depth: p.end };
      });

      return {
        pageIndex,
        pageStart,
        pageEnd,
        pageRange,
        tickValues,
        prepared,
        boundaryOffsets,
      };
    },
  );

  return (
    <div
      id={
        groupMode
          ? `borehole-doc-${borehole.id}`
          : isPreview
            ? `borehole-preview-${borehole.id}`
            : `borehole-pdf-${borehole.id}`
      }
      style={
        groupMode
          ? { position: "static", width: "794px" }
          : {
              position: isPreview ? "relative" : "absolute",
              left: isPreview ? "0" : "-9999px",
              top: 0,
              width: "794px",
            }
      }
    >
      {pages.map(page => (
        <div
          key={`page-${page.pageIndex}`}
          style={{
            width: "794px",
            minHeight: "1123px",
            padding: "30px",
            boxSizing: "border-box",
            backgroundColor: "#fff",
            color: "#000",
            fontFamily: '"Times New Roman Bold", "Times New Roman", serif',
            margin: isPreview ? "0 auto 16px auto" : "0",
            overflow: "hidden",
            breakAfter: page.pageIndex < pages.length - 1 ? "page" : "auto",
            pageBreakAfter:
              page.pageIndex < pages.length - 1 ? "always" : "auto",
          }}
        >
          <div
            style={{
              border: "5px solid #000",
              padding: "3px",
              minHeight: "1053px",
            }}
          >
            <div
              style={{
                border: "2px solid #000",
                minHeight: "1043px",
                padding: "15px",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundImage: `url(${headerLogo})`,
                  backgroundSize: "80%",
                  backgroundPosition: "center center",
                  backgroundRepeat: "no-repeat",
                  opacity: 0.08,
                  zIndex: 0,
                  pointerEvents: "none",
                }}
              />

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  marginBottom: "10px",
                }}
              >
                <img
                  src={`${publicUrl}/HeaderAce.png`}
                  alt="Header"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderBottom: "3px double #000",
                    paddingBottom: "5px",
                  }}
                  crossOrigin="anonymous"
                />
              </div>

              <div
                style={{
                  position: "relative",
                  zIndex: 1,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  direction: dir,
                  textAlign,
                }}
              >
                <h1
                  style={{
                    textAlign: "center",
                    fontSize: "15px",
                    margin: "0 0 4px 0",
                    fontWeight: "bold",
                  }}
                >
                  {isRTL
                    ? `سجل جسة رقم ${boreholeNo}`
                    : `Borehole Log No. ${boreholeNo}`}
                </h1>
                {pages.length > 1 && (
                  <div
                    style={{
                      textAlign: "center",
                      fontSize: "10px",
                      marginBottom: "6px",
                    }}
                  >
                    {isRTL
                      ? `صفحة ${page.pageIndex + 1} من ${pages.length}`
                      : `Page ${page.pageIndex + 1} of ${pages.length}`}
                  </div>
                )}

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    marginBottom: "8px",
                    fontSize: "9px",
                  }}
                >
                  <tbody>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "المشروع" : "Project"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(
                          borehole.project_name ||
                            borehole.project ||
                            borehole.project_id,
                        )}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "التاريخ" : "Date"}
                      </td>
                      <td style={reportValueStyle}>
                        {formatDate(borehole.report_date)}
                      </td>
                    </tr>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "المالك" : "Owner"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.owner)}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "طريقة الحفر" : "Boring Method"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.boring_method)}
                      </td>
                    </tr>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "المقاول الرئيسي" : "Main Contractor"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.main_contractor)}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "العمق الكلي (م)" : "Total Depth, m"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.total_depth)}
                      </td>
                    </tr>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "الموقع" : "Location"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.location)}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "القطر، مم" : "Diameter, mm"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.diameter)}
                      </td>
                    </tr>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "الاستشاري" : "Consultant"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.consultant)}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "منسوب المياه" : "Groundwater"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.groundwater_level)}
                      </td>
                    </tr>
                    <tr>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "رقم مرجع ACE" : "ACE Ref. No"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.ace_ref_no)}
                      </td>
                      <td style={reportLabelStyle(isRTL)}>
                        {isRTL ? "رقم الجسة" : "BH No"}
                      </td>
                      <td style={reportValueStyle}>
                        {getDisplayValue(borehole.borehole_number)}
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "8px",
                    textAlign: "center",
                    tableLayout: "fixed",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#f0f0f0" }}>
                      <th style={{ ...tableHeaderStyle, width: "9%" }}>
                        {isRTL ? "العمق الكلي" : "Total Depth"}
                      </th>
                      <th style={{ ...tableHeaderStyle, width: "7%" }}>
                        {isRTL ? "نهاية الطبقة (م)" : "Layer end, m"}
                      </th>
                      <th style={{ ...tableHeaderStyle, width: "32%" }}>
                        {isRTL ? "وصف الطبقات" : "Layers Description"}
                      </th>
                      <th style={{ ...tableHeaderStyle, width: "13%" }}>
                        {isRTL ? "الدليل" : "Legend"}
                      </th>
                      <th style={{ ...tableHeaderStyle, width: "9%" }}>SPT-N</th>
                      <th style={{ ...tableHeaderStyle, width: "10%" }}>RQD%</th>
                      <th style={{ ...tableHeaderStyle, width: "10%" }}>TCR%</th>
                      <th style={{ ...tableHeaderStyle, width: "10%" }}>UCS/PN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.prepared.length > 0 ? (
                      page.prepared.map(
                        ({ layer: l, start, end, renderHeight }, i) => (
                          <tr key={i} style={{ height: `${renderHeight}px` }}>
                            {i === 0 && (
                              <td
                                rowSpan={page.prepared.length}
                                style={{
                                  ...tableCellStyle,
                                  padding: "0",
                                  verticalAlign: "top",
                                }}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    height: `${rulerHeight}px`,
                                    width: "100%",
                                    minWidth: "70px",
                                    backgroundColor: "#fff",
                                  }}
                                >
                                  {page.prepared.map(
                                    ({ layer: l, start, end }, bandIdx) => {
                                      const bandTop =
                                        ((start - page.pageStart) /
                                          page.pageRange) *
                                        rulerHeight;
                                      const bandHeight = Math.max(
                                        1,
                                        ((end - start) / page.pageRange) *
                                          rulerHeight,
                                      );
                                      const legendId =
                                        l?.legend?.id ?? l?.legend_id;
                                      const preciseColor = legendId
                                        ? legendColors[String(legendId)]
                                        : null;
                                      const bandColor =
                                        preciseColor ||
                                        getLayerColor(
                                          getLegendLabel(l, isRTL) ||
                                            l.layer_description,
                                        );
                                      return (
                                        <div
                                          key={`band-${page.pageIndex}-${bandIdx}`}
                                          style={{
                                            position: "absolute",
                                            top: `${bandTop}px`,
                                            height: `${bandHeight}px`,
                                            insetInlineStart: 4,
                                            width: "14px",
                                            background: bandColor,
                                            opacity: 0.85,
                                            borderRadius: "3px",
                                          }}
                                        />
                                      );
                                    },
                                  )}
                                  {page.tickValues.map(
                                    (depthValue, tickIndex) => {
                                      const ratio =
                                        (depthValue - page.pageStart) /
                                        page.pageRange;
                                      const top = ratio * rulerHeight;
                                      const isLast =
                                        tickIndex ===
                                        page.tickValues.length - 1;
                                      return (
                                        <div
                                          key={`tick-${page.pageIndex}-${tickIndex}-${depthValue}`}
                                          style={{
                                            position: "absolute",
                                            top: `${isLast ? rulerHeight - 1 : top}px`,
                                            left: 0,
                                            right: 0,
                                          }}
                                        >
                                          <div
                                            style={{
                                              borderTop: "1px solid #d9d9d9",
                                            }}
                                          />
                                          <div
                                            style={{
                                              position: "absolute",
                                              left: "20px",
                                              top: "-6px",
                                              fontSize: "8px",
                                              color: "#555",
                                            }}
                                          >
                                            {depthValue.toFixed(1)}
                                          </div>
                                        </div>
                                      );
                                    },
                                  )}
                                  {/* Intentionally no red boundary lines in Total Depth ruler */}
                                  {(() => {
                                    const gwDepth = toNumber(
                                      borehole.groundwater_level,
                                    );
                                    if (
                                      gwDepth === null ||
                                      gwDepth < page.pageStart ||
                                      gwDepth > page.pageEnd
                                    )
                                      return null;
                                    const ratio =
                                      (gwDepth - page.pageStart) /
                                      page.pageRange;
                                    const top = ratio * rulerHeight;
                                    return (
                                      <div
                                        style={{
                                          position: "absolute",
                                          top: `${top}px`,
                                          left: 0,
                                          right: 0,
                                          zIndex: 2,
                                        }}
                                      >
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            transform: "translateY(-100%)",
                                          }}
                                        >
                                          <span
                                            style={{
                                              fontSize: "8px",
                                              fontWeight: "bold",
                                              color: "#1565c0",
                                              whiteSpace: "nowrap",
                                            }}
                                          >
                                            I = {gwDepth.toFixed(2)}
                                          </span>
                                          <div
                                            style={{
                                              width: 0,
                                              height: 0,
                                              borderLeft:
                                                "5px solid transparent",
                                              borderRight:
                                                "5px solid transparent",
                                              borderTop: "7px solid #1565c0",
                                            }}
                                          />
                                          <div
                                            style={{
                                              width: "16px",
                                              height: "1px",
                                              backgroundColor: "#1565c0",
                                              marginTop: "1px",
                                            }}
                                          />
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </td>
                            )}
                            <td
                              style={{
                                ...tableCellStyle,
                                verticalAlign: "bottom",
                                paddingBottom: "2px",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {end.toFixed(1)}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                textAlign: "center",
                                height: `${renderHeight}px`,
                                padding: "0",
                                verticalAlign: "middle",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              <div
                                style={{
                                  height: `${Math.max(1, renderHeight - 2)}px`,
                                  overflow: "hidden",
                                  padding: "4px 3px",
                                  boxSizing: "border-box",
                                  lineHeight: "1.2",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                {getDisplayValue(l.layer_description)}
                              </div>
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                backgroundColor: getLegendImageUrl(l.legend)
                                  ? "#fff"
                                  : getLayerColor(
                                      getLegendLabel(l, isRTL) || l.layer_description,
                                    ),
                                fontWeight: "bold",
                                height: `${renderHeight}px`,
                                padding: "0",
                                verticalAlign: "top",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {getLegendImageUrl(l.legend) ? (
                                <div
                                  style={{
                                    height: `${Math.max(1, renderHeight - 2)}px`,
                                    backgroundImage: `url(${getLegendImageUrl(l.legend)})`,
                                    backgroundRepeat: "repeat",
                                    backgroundSize: "24px 24px",
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    height: `${Math.max(1, renderHeight - 2)}px`,
                                    overflow: "hidden",
                                    padding: "4px 3px",
                                    boxSizing: "border-box",
                                    fontSize: "8px",
                                  }}
                                >
                                  {getLegendLabel(l, isRTL)}
                                </div>
                              )}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                height: `${renderHeight}px`,
                                padding: "2px 4px",
                                verticalAlign: "middle",
                                fontSize: "8px",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {renderMultiValues(l.spt_n)}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                height: `${renderHeight}px`,
                                padding: "2px 4px",
                                verticalAlign: "middle",
                                fontSize: "8px",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {renderMultiValues(l.rqd_perc)}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                height: `${renderHeight}px`,
                                padding: "2px 4px",
                                verticalAlign: "middle",
                                fontSize: "8px",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {renderMultiValues(l.tcr_perc)}
                            </td>
                            <td
                              style={{
                                ...tableCellStyle,
                                height: `${renderHeight}px`,
                                padding: "2px 4px",
                                verticalAlign: "middle",
                                fontSize: "8px",
                                borderBottom: `${boundaryLineWidth}px solid #c31717`,
                              }}
                            >
                              {renderMultiValues(l.ucs_pn)}
                            </td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td colSpan="8" style={tableCellStyle}>
                          {isRTL ? "لا توجد بيانات طبقات" : "No layers data"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {page.pageIndex === pages.length - 1 && (
                  <div style={{ textAlign: "center", fontSize: "9px", margin: "4px 0" }}>
                    {isRTL
                      ? `نهاية الحفر عند ${getDisplayValue(borehole.total_depth)} م`
                      : `End of boring @ ${getDisplayValue(borehole.total_depth)} m`}
                  </div>
                )}

                {borehole.notes && page.pageIndex === pages.length - 1 && (
                  <div
                    style={{
                      marginTop: "8px",
                      padding: "8px",
                      border: "1px dashed #777",
                      fontSize: "9px",
                    }}
                  >
                    <strong>{isRTL ? "ملاحظات:" : "Notes:"}</strong>{" "}
                    {getDisplayValue(borehole.notes)}
                  </div>
                )}
              </div>

              <div
                style={{ position: "relative", zIndex: 1, marginTop: "auto" }}
              >
                <img
                  src={`${publicUrl}/FooterAce.png`}
                  alt="Footer"
                  style={{
                    width: "100%",
                    height: "auto",
                    borderTop: "2px solid #000",
                    paddingTop: "5px",
                  }}
                  crossOrigin="anonymous"
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "8px",
                    marginTop: "5px",
                  }}
                >
                  <span>
                    {isRTL
                      ? "مكتب البرقاوي مهندسون استشاريون"
                      : "Albaraqawi Consulting Engineers"}
                  </span>
                  <span>{new Date().toLocaleDateString("en-US")}</span>
                </div>
              </div>
            </div>
          </div>
          {page.pageIndex === pages.length - 1 && (
            <div
              style={{
                textAlign: "center",
                fontSize: "10px",
                fontStyle: "italic",
                color: "#1a2c66",
                marginTop: "6px",
              }}
            >
              {isRTL
                ? `شكل ١. سجل جسة رقم ${boreholeNo}.`
                : `Figure 1. Boring Log for borehole No. ${boreholeNo}.`}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// --- COMPONENT: All Boreholes List Template ---
const AllBoreholesPrintTemplate = ({
  boreholes,
  isRTL,
  t,
  publicUrl,
  headerLogo,
  from,
  searchTerm,
  isPreview = false,
}) => {
  const dir = isRTL ? "rtl" : "ltr";
  const textAlign = isRTL ? "right" : "left";

  return (
    <div
      id={isPreview ? "all-boreholes-preview" : "all-boreholes-pdf"}
      style={{
        width: "794px",
        minHeight: "1123px",
        padding: "30px",
        boxSizing: "border-box",
        backgroundColor: "#fff",
        color: "#000",
        fontFamily: '"Times New Roman Bold", "Times New Roman", serif',
        position: isPreview ? "relative" : "absolute",
        left: isPreview ? "0" : "-9999px",
        margin: isPreview ? "0 auto" : "0",
      }}
    >
      <div
        style={{
          border: "5px solid #000",
          padding: "3px",
          minHeight: "1053px",
        }}
      >
        <div
          style={{
            border: "2px solid #000",
            minHeight: "1043px",
            padding: "15px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundImage: `url(${headerLogo})`,
              backgroundSize: "80%",
              backgroundPosition: "center center",
              backgroundRepeat: "no-repeat",
              opacity: 0.08,
              zIndex: 0,
              pointerEvents: "none",
            }}
          />

          <img
            src={`${publicUrl}/HeaderAce.png`}
            alt="Header"
            style={{
              width: "100%",
              height: "auto",
              borderBottom: "4px double #000",
              paddingBottom: "10px",
            }}
            crossOrigin="anonymous"
          />

          <div
            style={{
              flex: 1,
              padding: "10px 0",
              zIndex: 1,
              direction: dir,
              textAlign,
            }}
          >
            <h1
              style={{
                textAlign: "center",
                fontSize: "18px",
                marginBottom: "4px",
                fontWeight: "bold",
                color: "#000080",
              }}
            >
              {isRTL ? "كشف تقارير جسات التربة" : "Boreholes Soil Reports List"}
            </h1>
            <p style={{ textAlign: "center", fontSize: "12px", margin: "0 0 4px", color: "#333", fontFamily: '"Times New Roman", serif' }}>
              {new Date().toLocaleDateString(isRTL ? "ar-SA" : "en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            {searchTerm && (
              <div style={{ textAlign: "center", fontSize: "11px", marginBottom: "8px", color: "#555", direction: dir }}>
                {`${t("boreholes.search", { defaultValue: "بحث" })}: ${searchTerm}`}
              </div>
            )}
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "9px",
                textAlign: "center",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f0f0f0" }}>
                  <th style={tableHeaderStyle}>{isRTL ? "م" : "No."}</th>
                  <th style={tableHeaderStyle}>
                    {isRTL ? "المشروع" : "Project"}
                  </th>
                  <th style={tableHeaderStyle}>{isRTL ? "المالك" : "Owner"}</th>
                  <th style={tableHeaderStyle}>
                    {isRTL ? "الموقع" : "Location"}
                  </th>
                  <th style={tableHeaderStyle}>
                    {isRTL ? "طريقة الحفر" : "Method"}
                  </th>
                  <th style={tableHeaderStyle}>
                    {isRTL ? "رقم الجسة" : "BH No"}
                  </th>
                  <th style={tableHeaderStyle}>{isRTL ? "العمق" : "Depth"}</th>
                  <th style={tableHeaderStyle}>{isRTL ? "التاريخ" : "Date"}</th>
                </tr>
              </thead>
              <tbody>
                {boreholes.map((b, i) => (
                  <tr key={b.id}>
                    <td style={tableCellStyle}>{from + i}</td>
                    <td
                      style={{
                        ...tableCellStyle,
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {getDisplayValue(
                        b.project_name || b.project || b.project_id,
                      )}
                    </td>
                    <td
                      style={{
                        ...tableCellStyle,
                        textAlign: isRTL ? "right" : "left",
                      }}
                    >
                      {getDisplayValue(b.owner)}
                    </td>
                    <td style={tableCellStyle}>
                      {getDisplayValue(b.location)}
                    </td>
                    <td style={tableCellStyle}>
                      {getDisplayValue(b.boring_method)}
                    </td>
                    <td style={tableCellStyle}>
                      {getDisplayValue(b.borehole_number)}
                    </td>
                    <td style={tableCellStyle}>
                      {getDisplayValue(b.total_depth)} {isRTL ? "م" : "m"}
                    </td>
                    <td style={tableCellStyle}>
                      {getDisplayValue(b.report_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <img
            src={`${publicUrl}/FooterAce.png`}
            alt="Footer"
            style={{
              width: "100%",
              height: "auto",
              marginTop: "auto",
              borderTop: "2px solid #000",
              paddingTop: "5px",
            }}
            crossOrigin="anonymous"
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "7px",
              marginTop: "3px",
              position: "relative",
              zIndex: 1,
            }}
          >
            <span>
              {isRTL
                ? "مكتب البرقاوي مهندسون استشاريون"
                : "Albaraqawi Consulting Engineers"}
            </span>
            <span>{new Date().toLocaleDateString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Styles for Print Templates ---
const reportLabelStyle = isRTL => ({
  border: "1px solid #000",
  padding: "4px 8px",
  fontWeight: "bold",
  backgroundColor: "#f9f9f9",
  width: "120px",
  textAlign: isRTL ? "right" : "left",
});

const reportValueStyle = {
  border: "1px solid #000",
  padding: "4px 8px",
  textAlign: "center",
};

const tableHeaderStyle = {
  border: "1px solid #000",
  padding: "5px 4px",
  fontWeight: "bold",
};

const tableCellStyle = {
  border: "1px solid #000",
  padding: "4px 4px",
};

const thStyle = {
  padding: "15px",
  textAlign: "center",
  color: "white",
  fontWeight: "bold",
  fontSize: "14px",
  borderBottom: "1px solid #333",
  backgroundColor: "#AC2000",
};

const tdStyle = {
  padding: "15px",
  color: "white",
  fontWeight: "500",
  textAlign: "center",
  opacity: 0.9,
};

const ActionButton = ({ children, onClick, title, bg = "#2f3644" }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: "white",
      border: "none",
      borderRadius: "6px",
      padding: "8px 10px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: "38px",
    }}
  >
    {children}
  </button>
);

const quickInputStyle = {
  width: "100%",
  padding: "10px 12px",
  backgroundColor: "#14263b",
  border: "1px solid #333",
  borderRadius: "8px",
  color: "white",
  fontSize: "14px",
  boxSizing: "border-box",
};

export default Boreholes;
